import { supabase } from './supabase';
import { db } from './localDb';
import { connectionStatus, isOnline } from '../utils/offline';

// PostgREST defaults to a max-rows cap (1,000 in Supabase). Without paging,
// list queries silently truncate — which broke sequential study for sources
// exceeding 1,000 cards. fetchAllPaginated loops .range() until we get a
// short page and concatenates the results.
const PAGE_SIZE = 1000;
async function fetchAllPaginated<T>(
  buildQuery: () => any
): Promise<{ data: T[]; error: any }> {
  const all: T[] = [];
  let from = 0;
  // safety cap: 1M rows (1,000 pages). Real tables will be much smaller.
  for (let page = 0; page < 1000; page++) {
    const to = from + PAGE_SIZE - 1;
    const { data, error } = await buildQuery().range(from, to);
    if (error) return { data: all, error };
    if (!data || data.length === 0) break;
    all.push(...(data as T[]));
    if (data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }
  return { data: all, error: null };
}

export interface SyncStatus {
  isSyncing: boolean;
  lastSync: Date | null;
  pendingChanges: number;
  error: string | null;
}

class SyncManager {
  private syncInterval: NodeJS.Timeout | null = null;
  private listeners: ((status: SyncStatus) => void)[] = [];
  private currentStatus: SyncStatus = {
    isSyncing: false,
    lastSync: null,
    pendingChanges: 0,
    error: null
  };

  constructor() {
    // Listen for online/offline changes
    connectionStatus.subscribe((online) => {
      if (online) {
        console.log('Connection restored, starting sync...');
        this.syncAll();
      }
    });
  }

  // Subscribe to sync status updates
  subscribe(callback: (status: SyncStatus) => void) {
    this.listeners.push(callback);
    callback(this.currentStatus); // Send current status immediately
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  private updateStatus(updates: Partial<SyncStatus>) {
    this.currentStatus = { ...this.currentStatus, ...updates };
    this.listeners.forEach(listener => listener(this.currentStatus));
  }

  // Initial data download for new users
  async initialSync(userId: string): Promise<void> {
    if (!isOnline()) {
      throw new Error('Cannot perform initial sync while offline');
    }

    this.updateStatus({ isSyncing: true, error: null });

    try {
      console.log('Starting initial sync...');

      // Download all cards (no user_id filter - cards are shared)
      const { data: cards, error: cardsError } = await fetchAllPaginated<any>(
        () => supabase.from('cards').select('*')
      );

      if (cardsError) throw cardsError;

      // Download all progress
      const { data: progress, error: progressError } = await fetchAllPaginated<any>(
        () => supabase.from('user_card_progress').select('*').eq('user_id', userId)
      );

      if (progressError) throw progressError;

      // Download all study sessions
      const { data: sessions, error: sessionsError } = await fetchAllPaginated<any>(
        () => supabase.from('study_sessions').select('*').eq('user_id', userId)
      );

      if (sessionsError) throw sessionsError;

      // Download all lessons (published for regular users, all for admins)
      const { data: profile } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', userId)
        .single();
      
      const isAdmin = profile?.is_admin || false;
      
      const { data: lessons, error: lessonsError } = await fetchAllPaginated<any>(
        () => {
          let q = supabase.from('lessons').select('*');
          if (!isAdmin) q = q.eq('is_published', true);
          return q;
        }
      );
      if (lessonsError) throw lessonsError;

      // Download lesson cards
      const { data: lessonCards, error: lessonCardsError } = await fetchAllPaginated<any>(
        () => supabase.from('lesson_cards').select('*')
      );
      if (lessonCardsError) throw lessonCardsError;

      // Download user lesson progress
      const { data: lessonProgress, error: lessonProgressError } = await fetchAllPaginated<any>(
        () => supabase.from('user_lesson_progress').select('*').eq('user_id', userId)
      );
      if (lessonProgressError) throw lessonProgressError;

      // Download all sources
      const { data: sources, error: sourcesError } = await fetchAllPaginated<any>(
        () => supabase.from('sources').select('*')
      );
      if (sourcesError) throw sourcesError;

      // Download all exercises
      const { data: exercises, error: exercisesError } = await fetchAllPaginated<any>(
        () => supabase.from('exercises').select('*')
      );
      if (exercisesError) throw exercisesError;

      // Download exercise_lessons
      const { data: exerciseLessons, error: exerciseLessonsError } = await fetchAllPaginated<any>(
        () => supabase.from('exercise_lessons').select('*')
      );
      if (exerciseLessonsError) throw exerciseLessonsError;

      // Save to local DB - use bulkPut instead of bulkAdd to handle existing records
      if (cards && cards.length > 0) {
        await db.cards.bulkPut(cards.map(card => ({
          ...card,
          _syncStatus: 'synced'
        })));
      }

      if (progress && progress.length > 0) {
        await db.userCardProgress.bulkPut(progress.map(p => ({
          ...p,
          _syncStatus: 'synced'
        })));
      }

      if (sessions && sessions.length > 0) {
        await db.studySessions.bulkPut(sessions.map(s => ({
          ...s,
          _syncStatus: 'synced'
        })));
      }

      if (lessons && lessons.length > 0) {
        await db.lessons.bulkPut(lessons.map(l => ({
          ...l,
          _syncStatus: 'synced'
        })));
      }

      if (lessonCards && lessonCards.length > 0) {
        await db.lessonCards.bulkPut(lessonCards.map(lc => ({
          ...lc,
          _syncStatus: 'synced'
        })));
      }

      if (lessonProgress && lessonProgress.length > 0) {
        await db.userLessonProgress.bulkPut(lessonProgress.map(lp => ({
          ...lp,
          _syncStatus: 'synced'
        })));
      }

      if (sources && sources.length > 0) {
        await db.sources.bulkPut(sources.map(s => ({
          ...s,
          _syncStatus: 'synced'
        })));
      }

      if (exercises && exercises.length > 0) {
        await db.exercises.bulkPut(exercises.map(e => ({
          ...e,
          _syncStatus: 'synced'
        })));
      }

      if (exerciseLessons && exerciseLessons.length > 0) {
        await db.exerciseLessons.bulkPut(exerciseLessons.map(el => ({
          ...el,
          _syncStatus: 'synced'
        })));
      }

      // Update sync metadata
      const now = new Date();
      await db.syncMetadata.where('tableName').equals('cards').modify({ lastSync: now });
      await db.syncMetadata.where('tableName').equals('user_card_progress').modify({ lastSync: now });
      await db.syncMetadata.where('tableName').equals('study_sessions').modify({ lastSync: now });
      await db.syncMetadata.where('tableName').equals('lessons').modify({ lastSync: now });
      await db.syncMetadata.where('tableName').equals('lesson_cards').modify({ lastSync: now });
      await db.syncMetadata.where('tableName').equals('user_lesson_progress').modify({ lastSync: now });
      await db.syncMetadata.where('tableName').equals('sources').modify({ lastSync: now });
      await db.syncMetadata.where('tableName').equals('exercises').modify({ lastSync: now });
      await db.syncMetadata.where('tableName').equals('exercise_lessons').modify({ lastSync: now });

      console.log(`Initial sync complete: ${cards?.length || 0} cards, ${progress?.length || 0} progress records, ${sessions?.length || 0} sessions, ${lessons?.length || 0} lessons, ${sources?.length || 0} sources, ${exercises?.length || 0} exercises`);
      
      this.updateStatus({ 
        isSyncing: false, 
        lastSync: now,
        pendingChanges: 0
      });
    } catch (error: any) {
      console.error('Initial sync failed:', error);
      let errorMessage = 'Sync failed';
      
      if (error.message?.includes('Key already exists')) {
        errorMessage = 'Some data already exists locally. Sync completed partially.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      this.updateStatus({ 
        isSyncing: false, 
        error: errorMessage
      });
      
      // Don't throw for "already exists" errors - these are recoverable
      if (!error.message?.includes('Key already exists')) {
        throw error;
      }
    }
  }

  // Sync all pending changes and pull updates
  async syncAll(): Promise<void> {
    if (!isOnline() || this.currentStatus.isSyncing) {
      return;
    }

    this.updateStatus({ isSyncing: true, error: null });

    try {
      // Process sync queue (push local changes)
      await this.processSyncQueue();

      // Pull remote changes
      await this.pullRemoteChanges();

      // Update last sync time
      const now = new Date();
      this.updateStatus({ 
        isSyncing: false, 
        lastSync: now,
        pendingChanges: await this.getPendingChangesCount()
      });
    } catch (error) {
      console.error('Sync failed:', error);
      this.updateStatus({ 
        isSyncing: false, 
        error: String(error)
      });
    }
  }

  private async processSyncQueue(): Promise<void> {
    const queueItems = await db.syncQueue.toArray();
    
    for (const item of queueItems) {
      try {
        if (item.attempts > 3) {
          console.error(`Skipping item after 3 failed attempts:`, item);
          continue;
        }

        switch (item.tableName) {
          case 'cards':
            if (item.operation === 'delete') {
              await supabase.from('cards').delete().eq('id', item.recordId);
              await db.cards.delete(item.recordId);
            } else {
              await supabase.from('cards').upsert(item.data);
              await db.cards.update(item.recordId, { _syncStatus: 'synced' });
            }
            break;

          case 'user_card_progress':
            await supabase.from('user_card_progress').upsert(item.data);
            const [userId, cardId] = item.recordId.split('_');
            await db.userCardProgress.update([userId, cardId], { _syncStatus: 'synced' });
            break;

          case 'study_sessions':
            await supabase.from('study_sessions').upsert(item.data);
            await db.studySessions.update(item.recordId, { _syncStatus: 'synced' });
            break;

          case 'lessons':
            if (item.operation === 'delete') {
              await supabase.from('lessons').delete().eq('id', item.recordId);
              await db.lessons.delete(item.recordId);
            } else {
              await supabase.from('lessons').upsert(item.data);
              await db.lessons.update(item.recordId, { _syncStatus: 'synced' });
            }
            break;

          case 'lesson_cards':
            if (item.operation === 'delete') {
              const [lessonId, cardId] = item.recordId.split('_');
              await supabase.from('lesson_cards')
                .delete()
                .eq('lesson_id', lessonId)
                .eq('card_id', cardId);
              await db.lessonCards.where('[lesson_id+card_id]').equals([lessonId, cardId]).delete();
            } else {
              await supabase.from('lesson_cards').upsert(item.data);
              const [lessonId, cardId] = item.recordId.split('_');
              await db.lessonCards.update([lessonId, cardId], { _syncStatus: 'synced' });
            }
            break;

          case 'user_lesson_progress':
            await supabase.from('user_lesson_progress').upsert(item.data);
            const [uId, lId] = item.recordId.split('_');
            await db.userLessonProgress.update([uId, lId], { _syncStatus: 'synced' });
            break;

          case 'card_review_history':
            await supabase.from('card_review_history').insert(item.data);
            await db.cardReviewHistory.update(item.recordId, { _syncStatus: 'synced' });
            break;

          case 'sources':
            if (item.operation === 'delete') {
              await supabase.from('sources').delete().eq('id', item.recordId);
              await db.sources.delete(item.recordId);
            } else {
              await supabase.from('sources').upsert(item.data);
              await db.sources.update(item.recordId, { _syncStatus: 'synced' });
            }
            break;

          case 'exercises':
            if (item.operation === 'delete') {
              await supabase.from('exercises').delete().eq('id', item.recordId);
              await db.exercises.delete(item.recordId);
            } else {
              await supabase.from('exercises').upsert(item.data);
              await db.exercises.update(item.recordId, { _syncStatus: 'synced' });
            }
            break;

          case 'exercise_lessons':
            if (item.operation === 'delete') {
              const [exerciseId, lessonId] = item.recordId.split('_');
              await supabase.from('exercise_lessons')
                .delete()
                .eq('exercise_id', exerciseId)
                .eq('lesson_id', lessonId);
              await db.exerciseLessons.where('[exercise_id+lesson_id]').equals([exerciseId, lessonId]).delete();
            } else {
              await supabase.from('exercise_lessons').upsert(item.data);
              const [exerciseId, lessonId] = item.recordId.split('_');
              await db.exerciseLessons.update([exerciseId, lessonId], { _syncStatus: 'synced' });
            }
            break;
        }

        // Remove from queue on success
        await db.syncQueue.delete(item.id!);
      } catch (error) {
        console.error(`Failed to sync ${item.tableName} ${item.recordId}:`, error);
        // Increment attempts
        await db.syncQueue.update(item.id!, {
          attempts: item.attempts + 1,
          lastError: String(error)
        });
      }
    }
  }

  private async pullRemoteChanges(): Promise<void> {
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) return;

    // Get last sync times
    const cardsMeta = await db.syncMetadata.where('tableName').equals('cards').first();
    const progressMeta = await db.syncMetadata.where('tableName').equals('user_card_progress').first();
    const sessionsMeta = await db.syncMetadata.where('tableName').equals('study_sessions').first();
    const lessonsMeta = await db.syncMetadata.where('tableName').equals('lessons').first();
    const lessonCardsMeta = await db.syncMetadata.where('tableName').equals('lesson_cards').first();
    const lessonProgressMeta = await db.syncMetadata.where('tableName').equals('user_lesson_progress').first();
    const sourcesMeta = await db.syncMetadata.where('tableName').equals('sources').first();

    // Pull updated cards and detect deletions
    if (cardsMeta) {
      const { data: cards } = await fetchAllPaginated<any>(
        () => supabase.from('cards').select('*').gt('updated_at', cardsMeta.lastSync.toISOString())
      );

      if (cards) {
        for (const card of cards) {
          const existing = await db.cards.get(card.id);
          if (!existing || new Date(card.updated_at) > new Date(existing.updated_at)) {
            await db.cards.put({ ...card, _syncStatus: 'synced' });
          }
        }
      }

      // Detect server-side deletions: fetch ALL remote card IDs (paginated!) and remove local orphans
      const { data: remoteCardIds } = await fetchAllPaginated<{ id: string }>(
        () => supabase.from('cards').select('id')
      );

      if (remoteCardIds) {
        const remoteIdSet = new Set(remoteCardIds.map(r => r.id));
        const localCards = await db.cards.toArray();
        const pendingQueue = await db.syncQueue.where('tableName').equals('cards').toArray();
        const pendingIds = new Set(pendingQueue.map(q => q.recordId));

        for (const local of localCards) {
          // Remove if not on server and not pending local sync
          if (!remoteIdSet.has(local.id) && !pendingIds.has(local.id)) {
            await db.cards.delete(local.id);
          }
        }
      }
    }

    // Pull updated progress (with conflict resolution)
    if (progressMeta) {
      const { data: progress } = await fetchAllPaginated<any>(
        () => supabase.from('user_card_progress').select('*').eq('user_id', user.id).gt('updated_at', progressMeta.lastSync.toISOString())
      );

      if (progress) {
        for (const p of progress) {
          const existing = await db.userCardProgress.get([p.user_id, p.card_id]);
          if (!existing) {
            await db.userCardProgress.put({ ...p, _syncStatus: 'synced' });
          } else {
            // Conflict resolution: take highest mastery score
            const merged = {
              ...p,
              mastery_score: Math.max(existing.mastery_score, p.mastery_score),
              _syncStatus: 'synced' as const
            };
            await db.userCardProgress.put(merged);
          }
        }
      }
    }

    // Pull updated sessions
    if (sessionsMeta) {
      const { data: sessions } = await fetchAllPaginated<any>(
        () => supabase.from('study_sessions').select('*').eq('user_id', user.id).gt('started_at', sessionsMeta.lastSync.toISOString())
      );

      if (sessions) {
        for (const session of sessions) {
          await db.studySessions.put({ ...session, _syncStatus: 'synced' });
        }
      }
    }

    // Pull updated lessons and detect deletions
    if (lessonsMeta) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', user.id)
        .single();

      const isAdmin = profile?.is_admin || false;

      const { data: lessons } = await fetchAllPaginated<any>(
        () => {
          let q = supabase.from('lessons').select('*').gt('updated_at', lessonsMeta.lastSync.toISOString());
          if (!isAdmin) q = q.eq('is_published', true);
          return q;
        }
      );

      if (lessons) {
        for (const lesson of lessons) {
          const existing = await db.lessons.get(lesson.id);
          if (!existing || new Date(lesson.updated_at) > new Date(existing.updated_at)) {
            await db.lessons.put({ ...lesson, _syncStatus: 'synced' });
          }
        }
      }

      // Detect server-side lesson deletions
      const { data: remoteLessonIds } = await fetchAllPaginated<{ id: string }>(
        () => {
          let q = supabase.from('lessons').select('id');
          if (!isAdmin) q = q.eq('is_published', true);
          return q;
        }
      );

      if (remoteLessonIds) {
        const remoteIdSet = new Set(remoteLessonIds.map(r => r.id));
        const localLessons = await db.lessons.toArray();
        const pendingQueue = await db.syncQueue.where('tableName').equals('lessons').toArray();
        const pendingIds = new Set(pendingQueue.map(q => q.recordId));

        for (const local of localLessons) {
          if (!remoteIdSet.has(local.id) && !pendingIds.has(local.id)) {
            await db.lessons.delete(local.id);
          }
        }
      }
    }

    // Pull lesson cards — merge instead of clear-and-rebuild to preserve pending changes
    if (lessonCardsMeta) {
      const { data: lessonCards } = await fetchAllPaginated<any>(
        () => supabase.from('lesson_cards').select('*')
      );

      if (lessonCards) {
        // Upsert remote records
        await db.lessonCards.bulkPut(lessonCards.map(lc => ({
          ...lc,
          _syncStatus: 'synced' as const
        })));

        // Remove local records that no longer exist on server, but preserve pending local changes
        const remoteKeys = new Set(lessonCards.map(lc => `${lc.lesson_id}_${lc.card_id}`));
        const localLessonCards = await db.lessonCards.toArray();
        const pendingQueue = await db.syncQueue.where('tableName').equals('lesson_cards').toArray();
        const pendingIds = new Set(pendingQueue.map(q => q.recordId));

        for (const local of localLessonCards) {
          const key = `${local.lesson_id}_${local.card_id}`;
          if (!remoteKeys.has(key) && !pendingIds.has(key)) {
            await db.lessonCards.where('[lesson_id+card_id]').equals([local.lesson_id, local.card_id]).delete();
          }
        }
      }
    }

    // Pull updated lesson progress
    if (lessonProgressMeta) {
      const { data: lessonProgress } = await fetchAllPaginated<any>(
        () => supabase.from('user_lesson_progress').select('*').eq('user_id', user.id).gt('updated_at', lessonProgressMeta.lastSync.toISOString())
      );

      if (lessonProgress) {
        for (const progress of lessonProgress) {
          const existing = await db.userLessonProgress
            .where('[user_id+lesson_id]')
            .equals([progress.user_id, progress.lesson_id])
            .first();
          
          if (!existing || new Date(progress.updated_at) > new Date(existing.updated_at)) {
            await db.userLessonProgress.put({ ...progress, _syncStatus: 'synced' });
          }
        }
      }
    }

    // Pull updated sources and detect deletions
    if (sourcesMeta) {
      const { data: sources } = await fetchAllPaginated<any>(
        () => supabase.from('sources').select('*').gt('updated_at', sourcesMeta.lastSync.toISOString())
      );

      if (sources) {
        for (const source of sources) {
          const existing = await db.sources.get(source.id);
          if (!existing || new Date(source.updated_at) > new Date(existing.updated_at)) {
            await db.sources.put({ ...source, _syncStatus: 'synced' });
          }
        }
      }

      // Detect server-side source deletions
      const { data: remoteSourceIds } = await fetchAllPaginated<{ id: string }>(
        () => supabase.from('sources').select('id')
      );

      if (remoteSourceIds) {
        const remoteIdSet = new Set(remoteSourceIds.map((r: { id: string }) => r.id));
        const localSources = await db.sources.toArray();
        const pendingQueue = await db.syncQueue.where('tableName').equals('sources').toArray();
        const pendingIds = new Set(pendingQueue.map(q => q.recordId));

        for (const local of localSources) {
          if (!remoteIdSet.has(local.id) && !pendingIds.has(local.id)) {
            await db.sources.delete(local.id);
          }
        }
      }
    }

    // Pull updated exercises and detect deletions
    const exercisesMeta = await db.syncMetadata.where('tableName').equals('exercises').first();
    if (exercisesMeta) {
      const { data: exercises } = await fetchAllPaginated<any>(
        () => supabase.from('exercises').select('*').gt('updated_at', exercisesMeta.lastSync.toISOString())
      );

      if (exercises) {
        for (const exercise of exercises) {
          const existing = await db.exercises.get(exercise.id);
          if (!existing || new Date(exercise.updated_at) > new Date(existing.updated_at)) {
            await db.exercises.put({ ...exercise, _syncStatus: 'synced' });
          }
        }
      }

      // Detect deletions
      const { data: remoteExerciseIds } = await fetchAllPaginated<{ id: string }>(
        () => supabase.from('exercises').select('id')
      );
      if (remoteExerciseIds) {
        const remoteIdSet = new Set(remoteExerciseIds.map((r: { id: string }) => r.id));
        const localExercises = await db.exercises.toArray();
        const pendingQueue = await db.syncQueue.where('tableName').equals('exercises').toArray();
        const pendingIds = new Set(pendingQueue.map(q => q.recordId));
        for (const local of localExercises) {
          if (!remoteIdSet.has(local.id) && !pendingIds.has(local.id)) {
            await db.exercises.delete(local.id);
          }
        }
      }
    }

    // Pull exercise_lessons (merge approach like lesson_cards)
    const exerciseLessonsMeta = await db.syncMetadata.where('tableName').equals('exercise_lessons').first();
    if (exerciseLessonsMeta) {
      const { data: exerciseLessons } = await fetchAllPaginated<any>(
        () => supabase.from('exercise_lessons').select('*')
      );
      if (exerciseLessons) {
        await db.exerciseLessons.bulkPut(exerciseLessons.map(el => ({
          ...el,
          _syncStatus: 'synced' as const
        })));

        const remoteKeys = new Set(exerciseLessons.map(el => `${el.exercise_id}_${el.lesson_id}`));
        const localELs = await db.exerciseLessons.toArray();
        const pendingQueue = await db.syncQueue.where('tableName').equals('exercise_lessons').toArray();
        const pendingIds = new Set(pendingQueue.map(q => q.recordId));
        for (const local of localELs) {
          const key = `${local.exercise_id}_${local.lesson_id}`;
          if (!remoteKeys.has(key) && !pendingIds.has(key)) {
            await db.exerciseLessons.where('[exercise_id+lesson_id]').equals([local.exercise_id, local.lesson_id]).delete();
          }
        }
      }
    }

    // Update sync metadata
    const now = new Date();
    await db.syncMetadata.where('tableName').equals('cards').modify({ lastSync: now });
    await db.syncMetadata.where('tableName').equals('user_card_progress').modify({ lastSync: now });
    await db.syncMetadata.where('tableName').equals('study_sessions').modify({ lastSync: now });
    await db.syncMetadata.where('tableName').equals('lessons').modify({ lastSync: now });
    await db.syncMetadata.where('tableName').equals('lesson_cards').modify({ lastSync: now });
    await db.syncMetadata.where('tableName').equals('user_lesson_progress').modify({ lastSync: now });
    await db.syncMetadata.where('tableName').equals('card_review_history').modify({ lastSync: now });
    await db.syncMetadata.where('tableName').equals('sources').modify({ lastSync: now });
    await db.syncMetadata.where('tableName').equals('exercises').modify({ lastSync: now });
    await db.syncMetadata.where('tableName').equals('exercise_lessons').modify({ lastSync: now });
  }

  private async getPendingChangesCount(): Promise<number> {
    return await db.syncQueue.count();
  }

  // Start periodic sync
  startPeriodicSync(intervalMs: number = 5 * 60 * 1000) { // Default 5 minutes
    this.stopPeriodicSync();
    
    this.syncInterval = setInterval(() => {
      if (isOnline()) {
        this.syncAll();
      }
    }, intervalMs);

    // Also sync immediately
    if (isOnline()) {
      this.syncAll();
    }
  }

  // Stop periodic sync
  stopPeriodicSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  // Clear all local data (for logout)
  async clearLocalData() {
    await db.clearAllData();
    this.updateStatus({
      lastSync: null,
      pendingChanges: 0,
      error: null
    });
  }
}

// Export singleton instance
export const syncManager = new SyncManager();